import { useEffect, useRef } from 'react';
import { captchaKey } from '../../constants';

const ReCaptchaEnterprise = ({ onChange, onError }) => {
  const recaptchaRef = useRef(null);
  const recaptchaWidgetId = useRef(null);

  useEffect(() => {
    // Don't render if widget already exists or element has children
    if (
      recaptchaWidgetId.current ||
      (recaptchaRef.current && recaptchaRef.current.children.length > 0)
    ) {
      return;
    }

    const renderWidget = () => {
      // Double-check before rendering
      if (!recaptchaRef.current) {
        return;
      }
      if (recaptchaWidgetId.current) {
        return;
      }
      if (recaptchaRef.current.children.length > 0) {
        return;
      }
      if (!window.grecaptcha?.enterprise) {
        return;
      }

      try {
        if (!captchaKey) {
          console.error('reCAPTCHA: captchaKey is not defined');
          return;
        }
        recaptchaWidgetId.current = window.grecaptcha.enterprise.render(
          recaptchaRef.current,
          {
            sitekey: captchaKey,
            callback: () => {
              if (onChange) {
                onChange();
              }
            },
            'error-callback': (error) => {
              // Error callback may be called without an error parameter
              // Only log if there's an actual error message
              if (error) {
                console.error('reCAPTCHA error:', error);
              }
              if (onError) {
                onError();
              }
            },
            'expired-callback': () => {
              // Token has expired, disable the submit button
              if (onError) {
                onError();
              }
            },
          },
        );
      } catch (e) {
        // Widget might already be rendered, check if it's the "already rendered" error
        if (e.message?.includes('already been rendered')) {
          // Widget is already there, mark it as rendered
          recaptchaWidgetId.current = 'existing';
        } else {
          console.error('reCAPTCHA render error:', e);
        }
      }
    };

    // Check if script is already loaded
    if (document.querySelector('script[src*="recaptcha/enterprise.js"]')) {
      // Script already exists, just render the widget
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(renderWidget);
      } else {
        // Wait for grecaptcha to be available
        const checkInterval = setInterval(() => {
          if (window.grecaptcha?.enterprise) {
            clearInterval(checkInterval);
            window.grecaptcha.enterprise.ready(renderWidget);
          }
        }, 100);
        return () => clearInterval(checkInterval);
      }
      return;
    }

    // Load reCAPTCHA Enterprise script (for checkbox widget, no render parameter)
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/enterprise.js';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.grecaptcha?.enterprise) {
        window.grecaptcha.enterprise.ready(renderWidget);
      }
    };

    return () => {
      // Cleanup: reset and remove widget
      if (
        recaptchaWidgetId.current &&
        recaptchaWidgetId.current !== 'existing'
      ) {
        if (window.grecaptcha?.enterprise) {
          try {
            window.grecaptcha.enterprise.reset(recaptchaWidgetId.current);
          } catch {
            // Ignore errors during cleanup
          }
        }
        try {
          // Clear the DOM element
          if (recaptchaRef.current) {
            recaptchaRef.current.innerHTML = '';
          }
        } catch {
          // Ignore errors
        }
      }
      recaptchaWidgetId.current = null;
    };
  }, [onChange, onError]);

  return <div ref={recaptchaRef} />;
};

ReCaptchaEnterprise.displayName = 'ReCaptchaEnterprise';

export default ReCaptchaEnterprise;
